import type { CompletionMetricsSummary, CompletionMonthlyTraffic } from 'types';
import { CHANNEL_ROWS, OUTCOME_ROWS, SIGNING_STATS } from './CompletionMetrics.consts';
import {
	buildAxisTicks,
	buildTrafficBars,
	channelPieGradient,
	createFormatters,
	type NumberSeparators
} from './CompletionMetrics.helpers';

type SummaryProps = Readonly<{ separators: NumberSeparators; summary: CompletionMetricsSummary; t: (key: string) => string; }>;

export function OutcomeCard({ separators, summary, t }: SummaryProps) {
	const { formatCount, formatPercent } = createFormatters(separators);
	const rows = OUTCOME_ROWS.map(outcome => ({
		code: outcome.code,
		color: outcome.color,
		label: t(outcome.label),
		count: outcome.count(summary),
		percent: outcome.percent(summary)
	}));

	return (
		<section className="admin-metrics-card">
			<div className="admin-metrics-card__head">
				<h2 className="admin-metrics-card__title">{t('outcome_of_every_process')}</h2>
				<span className="admin-metrics-card__meta">{t('total').replace('{total}', formatCount(summary.totalProcesses))}</span>
			</div>
			<div className="admin-metrics__stack-bar">
				{rows
					.filter(row => row.count > 0)
					.map(row => (
						<div
							key={row.code}
							style={{ background: row.color, width: `${row.percent}%` }}
							title={`${row.label}: ${formatCount(row.count)} (${formatPercent(row.percent)})`}
						/>
					))}
			</div>
			<div className="admin-metrics__outcome-rows">
				{rows.map(row => (
					<div className="admin-metrics__outcome-row" key={row.code}>
						<span className="admin-metrics__dot" style={{ background: row.color }} />
						<span className="admin-metrics__outcome-label">{row.label}</span>
						<span className="admin-metrics__outcome-code">{row.code}</span>
						<span className="admin-metrics__outcome-count">{formatCount(row.count)}</span>
						<span className="admin-metrics__outcome-pct">{formatPercent(row.percent)}</span>
					</div>
				))}
			</div>
		</section>
	);
}

export function ActivePopulationCard({ separators, summary, t }: SummaryProps) {
	const { formatCount, formatPercent } = createFormatters(separators);
	const rate = Math.min(100, Math.max(0, summary.completedPercentExcludingCanceledExpired ?? 0));

	return (
		<section className="admin-metrics-card">
			<h2 className="admin-metrics-card__title">{t('completion_within_')}</h2>
			<p className="admin-metrics-card__note">
				{t('canceled_and_expired_')}
			</p>
			<div className="admin-metrics-progress">
				<div className="admin-metrics-progress__track">
					<div className="admin-metrics-progress__bar">
						<div className="admin-metrics-progress__fill" style={{ width: `${rate}%` }} />
					</div>
					<div className="admin-metrics-progress__labels">
						<span>{formatCount(summary.completedCount)} {t('completed').toLowerCase()}</span>
						<span>{formatCount(summary.activePopulationExcludingCanceledExpiredCount)} {t('active')}</span>
					</div>
				</div>
				<div className="admin-metrics-progress__rate">
					{formatPercent(summary.completedPercentExcludingCanceledExpired)}
				</div>
			</div>
			<div className="admin-metrics__signing">
				{SIGNING_STATS.map(stat => (
					<div className="admin-metrics__signing-stat" key={stat.label}>
						<div className="admin-metrics__signing-value">{formatCount(stat.count(summary))}</div>
						<div className="admin-metrics__signing-label">{t(stat.label)}</div>
					</div>
				))}
			</div>
		</section>
	);
}

export function ChannelCard({ separators, summary, t }: SummaryProps) {
	const { formatCount, formatPercent } = createFormatters(separators);
	return (
		<section className="admin-metrics-card">
			<h2 className="admin-metrics-card__title">{t('where_created')}</h2>
			<div className="admin-metrics__channels">
				<div className="admin-metrics-pie">
					<div className="admin-metrics-pie__disc" style={{ background: channelPieGradient(summary) }} />
					<div className="admin-metrics-pie__center">
						<span className="admin-metrics-pie__total">{formatCount(summary.totalProcesses)}</span>
						<span className="admin-metrics-pie__caption">{t('created')}</span>
					</div>
				</div>
				<div className="admin-metrics__channel-rows">
					{CHANNEL_ROWS.map(channel => (
						<div className="admin-metrics__channel-row" key={channel.label}>
							<span className="admin-metrics__dot" style={{ background: channel.color }} />
							<span className="admin-metrics__channel-label">{t(channel.label)}</span>
							<span className="admin-metrics__channel-count">{formatCount(channel.count(summary))}</span>
							<span className="admin-metrics__channel-pct">{formatPercent(channel.percent(summary))}</span>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

export function TrafficCard({
	rows,
	separators,
	t
}: Readonly<{ rows: CompletionMonthlyTraffic[]; separators: NumberSeparators; t: (key: string) => string; }>) {
	if (!rows.length) {
		return (
			<section className="admin-metrics-card">
				<h2 className="admin-metrics-card__title">{t('monthly_traffic')}</h2>
				<p className="admin-metrics-card__note">{t('no_monthly_breakdown_')}</p>
			</section>
		);
	}

	const bars = buildTrafficBars(rows, separators, t);
	const ticks = buildAxisTicks(rows);

	return (
		<section className="admin-metrics-card">
			<div className="admin-metrics-card__head">
				<h2 className="admin-metrics-card__title">{t('monthly_traffic')}</h2>
				<div className="admin-metrics__legend">
					<span className="admin-metrics__legend-item">
						<span className="admin-metrics__dot" style={{ background: '#c7d0f7' }} />
						{t('created')}
					</span>
					<span className="admin-metrics__legend-item">
						<span className="admin-metrics__dot" style={{ background: '#0b45e6' }} />
						{t('completed')}
					</span>
				</div>
			</div>
			<div className="admin-metrics-chart">
				{bars.map(bar => (
					<div className="admin-metrics-chart__col" key={bar.key} title={bar.title}>
						<div className="admin-metrics-chart__created" style={{ height: bar.createdHeight }}>
							<div className="admin-metrics-chart__completed" style={{ height: bar.completedHeight }} />
						</div>
					</div>
				))}
			</div>
			<div className="admin-metrics-chart__axis">
				{ticks.map(tick => (
					<span className="admin-metrics-chart__tick" key={tick.key} style={{ left: tick.left }}>
						{tick.label}
					</span>
				))}
			</div>
			<div className="admin-metrics-chart__rates">
				<div className="admin-metrics-chart__rates-title">{t('proportion_of_completed_processes')}</div>
				<div className="admin-metrics-chart__rates-bars">
					{bars.map(bar => (
						<div
							className="admin-metrics-chart__rate"
							key={bar.key}
							style={{ height: bar.rateHeight }}
							title={bar.rateTitle}
						/>
					))}
				</div>
			</div>
		</section>
	);
}
